<script setup>
import { computed, reactive, watch } from 'vue';
import {
  Checkmark16,
  ChevronRight16,
  WarningAltFilled16,
} from '@carbon/icons-vue';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'next/polaris/ui/collapsible';
import {
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
} from 'next/polaris/ui/sidebar';
import SidebarSortMenu from './SidebarSortMenu.vue';

// The menu of tier 2: pages, groups that open, dividers between blocks, a count or a warning at the end of a row.
// A group opens by itself when the current page is inside it: on mount and every time the current item changes
// (Home card, top bar trail, ⌘K, a link, or an item only known once the data arrives, like an inbox's channel
// type). Groups the person opened or closed by hand keep that state until the current item moves again.
// The lists carry `list-none`: the app's base CSS gives every ul a disc marker (_base.scss). The registry's badge, action and sub-list use physical sides (`right-1`,
// `border-l`); the `rtl:` classes here move them to the end side under RTL without touching the ported files.
const props = defineProps({
  items: { type: Array, required: true },
  isItemActive: { type: Function, required: true },
});

const emit = defineEmits(['navigate']);

// open state per group name; `:default-open` alone only ran on mount, so a later navigation left the group shut
const openGroups = reactive({});

// the current item inside a group, as "group/child" (isItemActive reads the route, so this follows it)
const activeInGroup = computed(() => {
  const group = props.items.find(item =>
    item.children?.some(child => props.isItemActive(child))
  );
  if (!group) return null;
  const child = group.children.find(item => props.isItemActive(item));
  return { group: group.name, key: `${group.name}/${child.name}` };
});

// Ponto de status do setup do Polaris AI (âmbar a fazer, azul atual, ✓ feito) no fim da linha do passo.
const statusDotClass = tone =>
  ({
    todo: 'bg-polaris-warning-foreground',
    current: 'bg-polaris-info-foreground',
  })[tone] || '';

watch(
  () => activeInGroup.value?.key,
  () => {
    if (activeInGroup.value) openGroups[activeInGroup.value.group] = true;
  },
  { immediate: true }
);
</script>

<template>
  <SidebarMenu class="list-none">
    <template v-for="item in items" :key="item.name">
      <!-- w-auto on the data variant: the registry's `w-full` there would push the line 16px past the column -->
      <SidebarSeparator
        v-if="item.separator"
        class="my-1 data-[orientation=horizontal]:w-auto"
      />
      <Collapsible
        v-else-if="item.children"
        as-child
        class="group/collapsible"
        :open="!!openGroups[item.name]"
        @update:open="value => (openGroups[item.name] = value)"
      >
        <!-- group/sidebar-section: SidebarSortMenu shows its trigger while the row is hovered -->
        <SidebarMenuItem class="group/sidebar-section">
          <CollapsibleTrigger as-child>
            <SidebarMenuButton>
              <component :is="item.icon" v-if="item.icon" aria-hidden="true" />
              <span>{{ item.label }}</span>
              <ChevronRight16
                class="ms-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 rtl:rotate-180"
                aria-hidden="true"
              />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <!-- a wrapper, not as-child: the sort menu's own root is `relative`, which would undo the action's
               `absolute` placement -->
          <SidebarMenuAction
            v-if="item.sort"
            as="div"
            class="hover:bg-transparent rtl:right-auto rtl:left-1"
          >
            <SidebarSortMenu
              :active-sort="item.sort.activeSort"
              :options="item.sort.sortOptions"
              @sort="item.sort.onSortChange"
            />
          </SidebarMenuAction>
          <CollapsibleContent>
            <SidebarMenuSub
              class="list-none rtl:-translate-x-px rtl:border-l-0 rtl:border-r"
            >
              <SidebarMenuSubItem
                v-for="child in item.children"
                :key="child.name"
              >
                <SidebarMenuSubButton as-child :is-active="isItemActive(child)">
                  <RouterLink
                    :to="child.to"
                    :aria-current="isItemActive(child) ? 'page' : undefined"
                    :title="child.label"
                    @click="emit('navigate')"
                  >
                    <component
                      :is="child.icon"
                      v-if="child.icon"
                      aria-hidden="true"
                    />
                    <span class="truncate">{{ child.label }}</span>
                    <span
                      v-if="child.note"
                      class="ms-auto shrink-0 type-polaris-caption text-polaris-muted-foreground"
                    >
                      {{ child.note }}
                    </span>
                    <Checkmark16
                      v-else-if="child.statusDot?.tone === 'done'"
                      class="ms-auto shrink-0 text-polaris-success-foreground"
                      role="img"
                      :aria-label="child.statusDot.label"
                      :title="child.statusDot.label"
                    />
                    <span
                      v-else-if="child.statusDot"
                      class="ms-auto size-2 shrink-0 rounded-full"
                      :class="statusDotClass(child.statusDot.tone)"
                      role="img"
                      :aria-label="child.statusDot.label"
                      :title="child.statusDot.label"
                    />
                    <WarningAltFilled16
                      v-else-if="child.warning"
                      v-tooltip.top-end="child.warning"
                      class="ms-auto shrink-0 text-polaris-warning-foreground"
                      role="img"
                      :aria-label="child.warning"
                    />
                    <span
                      v-else-if="child.badgeCount"
                      class="ms-auto type-polaris-caption font-medium tabular-nums"
                    >
                      {{ child.badgeCount }}
                    </span>
                  </RouterLink>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
      <SidebarMenuItem v-else>
        <SidebarMenuButton as-child :is-active="isItemActive(item)">
          <RouterLink
            :to="item.to"
            :aria-current="isItemActive(item) ? 'page' : undefined"
            @click="emit('navigate')"
          >
            <component :is="item.icon" v-if="item.icon" aria-hidden="true" />
            <span>{{ item.label }}</span>
            <Checkmark16
              v-if="item.statusDot?.tone === 'done'"
              class="ms-auto shrink-0 text-polaris-success-foreground"
              role="img"
              :aria-label="item.statusDot.label"
              :title="item.statusDot.label"
            />
            <span
              v-else-if="item.statusDot"
              class="ms-auto size-2 shrink-0 rounded-full"
              :class="statusDotClass(item.statusDot.tone)"
              role="img"
              :aria-label="item.statusDot.label"
              :title="item.statusDot.label"
            />
          </RouterLink>
        </SidebarMenuButton>
        <SidebarMenuBadge
          v-if="item.badgeCount"
          class="rtl:right-auto rtl:left-1"
        >
          {{ item.badgeCount }}
        </SidebarMenuBadge>
      </SidebarMenuItem>
    </template>
  </SidebarMenu>
</template>
